import { animate, query, stagger, style, transition, trigger } from '@angular/animations';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { AfterViewInit, Component, computed, ElementRef, input, OnDestroy, signal, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatRadioChange } from '@angular/material/radio';
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons';

import { faStar } from '@fortawesome/pro-solid-svg-icons';
import * as Hammer from 'hammerjs';
import { Observable, Subject, timer } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import { PreviewDevice, PreviewDevices, RatingType } from '@empathy/survey/base';
import { Question } from '@empathy/survey-core';

@Component({
  selector: 'empathy-carousel-rating-question-view',
  templateUrl: 'carousel-rating-question-view.component.html',
  styleUrls: ['./carousel-rating-question-view.component.scss', './_carousel-rating-question-view-theme.component.scss'],
  animations: [
    trigger('trigState', [
      transition(':enter', [style({ transform: 'translateX(100%)', opacity: 0 }), animate('250ms cubic-bezier(.17,.67,1,1.04)')]),
      transition(':leave', [
        style({ transform: 'translateX(-100%)', opacity: 1, display: 'none' }),
        animate('250ms cubic-bezier(.17,.67,1,1.04)', style({ opacity: 0, display: 'none' }))
      ])
    ]),
    trigger('stagger', [
      transition('false => true', [
        query('mat-radio-button', [
          style({ opacity: 0, transform: 'translateX(30%)' }),
          stagger(100, [animate('250ms', style({ opacity: 1, transform: 'translateX(0)' }))])
        ])
      ])
    ]),
    trigger('staggerRating', [
      transition('false => true', [
        query('mat-button-toggle-group', [
          style({ opacity: 0, transform: 'translateX(30%)' }),
          stagger(100, [animate('250ms', style({ opacity: 1, transform: 'translateX(0)' }))])
        ])
      ])
    ])
  ]
})
export class CarouselRatingQuestionViewComponent implements AfterViewInit, OnDestroy {
  @ViewChild('carouselSwipeContainer') myBtn: ElementRef;
  @ViewChild('starContainer', { static: false }) starContainer!: ElementRef;

  question = input<Question>();
  questionsGrp = input<FormGroup>();

  isPhone$: Observable<boolean>;

  activeCard$ = signal(0);
  applyStarsContainerMaxWidth$ = signal<boolean>(false);
  isWeb$ = signal<boolean>(false);
  maxXStarsRatingScale$ = signal(5);
  movingToNextCard$ = signal(false);
  visibleCards$ = computed(() => this.question().visibleCards);
  visibleOptions$ = computed(() => this.question().visibleOptions);

  faChevronLeft = faChevronLeft;
  faChevronRight = faChevronRight;

  readonly maxXRatingScale = 10;

  faStar = faStar;
  ratingType = RatingType;

  private destroyed = new Subject<void>();

  private readonly nextCardDelay = 500;
  private webBreakpoints = [Breakpoints.Large, Breakpoints.XLarge];
  private readonly breakpoints = {
    xs: parseInt(Breakpoints.XSmall.match(/\d+/)?.[0] || '0', 10),
    sm: parseInt(Breakpoints.Small.match(/\d+/)?.[0] || '0', 10)
  };
  private phoneBreakpoints = [Breakpoints.XSmall, Breakpoints.Small];
  private resizeObserver!: ResizeObserver;

  constructor(breakpointObserver: BreakpointObserver) {
    this.isPhone$ = breakpointObserver.observe([Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium, Breakpoints.Large, Breakpoints.XLarge]).pipe(
      takeUntil(this.destroyed),
      map((result) => {
        for (const queryPoint of Object.keys(result.breakpoints)) {
          if (result.breakpoints[queryPoint]) {
            this.isWeb$.set(this.webBreakpoints.includes(queryPoint));
            return this.phoneBreakpoints.includes(queryPoint);
          }
        }
      })
    );
  }

  ngAfterViewInit(): void {
    const switcher = this.myBtn.nativeElement;
    const mc = new Hammer(switcher);

    mc.on('swipeleft', () => {
      this.onNext();
    });

    mc.on('swiperight', () => {
      this.onPrev();
    });

    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        if (width > 0) {
          this.updateStars(width);
          this.applyStarsContainerMaxWidth$.set(width > this.breakpoints.sm);
        }
      }
    });

    if (this.starContainer) {
      this.resizeObserver.observe(this.starContainer.nativeElement);
    }
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  getQuestionGroupForm = (cardId: unknown): FormControl =>
    this.questionsGrp()
      .get(cardId as string)
      .get('value') as FormControl;

  onRateAndMoveToNextCard(cardId: unknown, rate: number): void {
    this.setValueAndMoveToNext(cardId, rate);
  }

  onMoveToNextCard(cardId: unknown, change: MatRadioChange): void {
    this.setValueAndMoveToNext(cardId, change.value);
  }

  onNext(): void {
    const currentCardAnswered = this.getQuestionGroupForm(this.question().visibleCards[this.activeCard$()].id).value != '';
    if (this.activeCard$() < this.question().visibleCards.length - 1 && currentCardAnswered) {
      this.activeCard$.update((value) => value + 1);
    }
  }

  onPrev(): void {
    if (this.activeCard$() > 0) {
      this.activeCard$.update((value) => value - 1);
    }
  }

  private setValueAndMoveToNext(cardId: unknown, newValue: string | number): void {
    this.getQuestionGroupForm(cardId as string).setValue(newValue);

    this.movingToNextCard$.set(true);

    timer(this.nextCardDelay)
      .pipe(
        tap(() => {
          this.movingToNextCard$.set(false);

          if (this.activeCard$() < this.question().visibleCards.length - 1) {
            this.activeCard$.update((value) => value + 1);
          }
        })
      )
      .subscribe();
  }

  private updateStars(width: number): void {
    if (this.isWeb$()) {
      if (width < PreviewDevices[PreviewDevice.Phone].width) {
        this.maxXStarsRatingScale$.set(4);
        return;
      } else {
        this.maxXStarsRatingScale$.set(10);
      }

      return;
    }

    if (width < this.breakpoints.xs) {
      this.maxXStarsRatingScale$.set(5);
    } else {
      this.maxXStarsRatingScale$.set(10);
    }
  }
}
